<template>
  <div
    v-on-clickaway="close"
    :class="[classes, { 'no-label': !label }]"
  >
    <div
      class="select__field-wrapper"
      :class="{ disabled: disabled , selected: !!selected}"
      @click="onActivate"
    >
      <div class="select__field">
                <div class="select__text">
                  {{ text }}
                </div>
        <ChevronIcon class="select__arrow" v-if="!hideArrow"/>
      </div>
      <label
        v-if="label"
        :for="id"
        class="select__label"
      >
        {{ label }}
        <span v-if="required" class="select__label-required">*</span>
      </label>
    </div>

    <div :class="['select__dropdown', `select__dropdown_scrollbar_${scrollbar}`]">
      <div
        v-for="(item, index) in filteredItems"
        :key="`select-item-${index}-in-${id}`"
        class="select__dropdown-item"
        @click="onItemSelect(item)"
      >
        {{ item[itemTextKey] }}
      </div>
    </div>

    <transition name="slide-from-top">
      <span
        v-if="error && errorMsg"
        class="select__error-msg"
      >
        {{ errorMsg }}
        <ErrorIcon/>
      </span>
    </transition>
  </div>
</template>

<script>
  import ChevronIcon from '@/assets/img/icons/arrow.svg?inline'
  import ErrorIcon from '@/assets/img/icons/error.svg?inline'
  import { mixin as clickaway } from 'vue-clickaway'
  import { get } from 'lodash'

  export default {
    name: 'BaseSelect',
    components: {
      ChevronIcon,
      ErrorIcon
    },
    mixins: [clickaway],
    props: {
      emitOnCreate: {
        type: Boolean,
        default: true
      },

      value: {
        type: null,
        default: ''
      },
      disabled: {
        type: [Boolean, Number, String],
        default: false
      },
      required: {
        type: Boolean,
        default: false
      },
      label: {
        type: String,
        default: ''
      },
      placeholder: {
        type: String,
        default: ''
      },
      items: {
        type: Array,
        requied: true
      },
      itemValueKey: {
        type: String,
        default: 'value'
      },
      itemTextKey: {
        type: String,
        default: 'text'
      },
      errorMsg: {
        type: String,
        default: ''
      },
      scrollbar: {
        type: String,
        default: 'custom'
      },
      disable: {
        type: Boolean,
        default: false
      },
      hideArrow: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        isActive: false,
        error: null
      }
    },
    computed: {
      selected () {
        return !this.value ? null : this.items.filter(({ value }) => value === this.value)[0]
      },
      id () {
        return `select-${this._uid}`
      },
      text () {
        return get(this, ['selected', this.itemTextKey]) || this.placeholder
      },
      classes () {
        return [
          'select',
          {
            'select_active': this.isActive,
            'select_error': this.error,
            'select_no-items': this.items.length < 1 || this.disable
          }
        ]
      },
      filteredItems () {
        const filteredItems = this.items.filter(item => item.value !== get(this, 'selected.value'))
        if (this.items.length === 1 && get(this, 'selected.value')) {
          filteredItems.push({ text: this.placeholder })
        }
        return filteredItems
      }
    },
    watch: {
      errorMsg: {
        handler (msg) {
          this.error = !!msg
        },
        immediate: true
      }
    },
    created () {
      if (!this.placeholder && this.emitOnCreate) {
        this.onItemSelect(this.items[0])
      }
    },
    methods: {
      onActivate () {
        this.isActive = !this.isActive
        this.error = false
      },
      onItemSelect (item) {
        const selected = item || {}
        this.$emit('input', selected[this.itemValueKey], selected)
        this.close()
      },
      close () {
        this.isActive = false
      }
    }
  }
</script>

<style lang="scss">
$padding-for-error: 30px;
$active-box-shadow: 2px 2px 5px rgba($c-black, 0.1);

.select {
  position: relative;
  display: block;
  min-width: 290px;
  // max-width: 335px;
  padding-bottom: $padding-for-error;
  cursor: pointer;
  height: max-content;

  @include mobile {
    max-width: 100%;
  }

  &__label {
    user-select: none;
    position: absolute;
    z-index: 1;
    top: 8px;
    left: 16px;
    font-size: 13px;
    transform: translateY(0);
    display: inline-block;
    //padding-bottom: 1em;
    font-family: "ToyotaType-Book", Arial, Helvetica, sans-serif;
    color: $c-gray-7;
    //transition: 0.35s;

    &-required {
      color: $c-errorDark;
    }

    @include mobile {
      font-size: 11px;
    }
  }

  &__text {
    position: relative;
    top: 6px;
    padding-right: 20px;
    color: $c-gray-dark;
    line-height: 1.5;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__field-wrapper {
    position: relative;

    &.disabled {
      pointer-events: none;

      .select__field {
        background: $c-gray-4;
      }

      .select__text {
        color: $c-gray-7;
      }
    }
  }

  &__field {
    position: relative;
    display: block;
    width: 100%;
    min-height: 58px;
    padding: 17px 19px 17px 16px;
    font-family: "ToyotaType-Book", Arial, Helvetica, sans-serif;
    font-size: 16px;
    line-height: 1;
    background-color: $c-white;
    border: 1px solid $c-gray-4;
    outline: none;
    caret-color: $c-active-light;
    transition: 0.3s;
    appearance: none;
    -webkit-appearance: none;

    &:focus {
      background-color: $c-gray-4;
    }

    @include hover {
      background-color: $c-gray-1;

      + .select__label {
        color: $c-gray-dark;
      }
    }
  }

  &__arrow {
    position: absolute;
    right: 16px;
    top: 50%;
    width: 20px;
    height: 20px;
    transition: 0.3s;
    transform: rotate(180deg) translateY(-50%) !important;
    transform-origin: top;

    @include mobile {
      top: 62%;
    }
  }

  &__dropdown {
    position: absolute;
    top: calc(100% - #{$padding-for-error});
    left: 0;
    z-index: 999;
    width: 100%;
    max-height: 270px;
    overflow-y: auto;
    visibility: hidden;
    opacity: 0;
    transition: 0.3s;
    -webkit-overflow-scrolling: touch;
    border-bottom: 1px solid $c-gray-4;

    &_scrollbar {
      &_custom {
        border-right: 1px solid $c-gray-4;
        background: $c-white;

        @include custom-scrollbar-inputs();
      }

      &_none {
        @include no-scrollbar();
      }
    }

    &-item {
      padding: 16px;
      font-size: 16px;
      line-height: 1.5;
      color: #333;
      background-color: $c-white;
      border: 1px solid $c-gray-4;
      border-bottom: 0;
      transition: background-color 0.3s;

      &:first-child {
        border-top: 0;
      }

      @include hover {
        background-color: $c-gray-1;
      }
    }
  }

  &__error-msg {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: absolute;
    left: 0;
    font-size: 12px;
    color: $c-errorDark;
    height: 20px;
    bottom: 4px;
    width: calc(100% - 30px);

    img,
    svg {
      display: flex;
      min-width: 14px;
      min-height: 15px;
      width: 14px;
      height: 15px;
      object-fit: contain;
    }
  }

  &_error {
    .select {
      &__field {
        border: 1px solid $c-errorDark;
      }
    }
  }

  &_no-items {
    pointer-events: none;

    .select__field {
      background: $c-gray-4;
    }

    .select__text {
      color: $c-gray-7;
    }
  }

  &_active {
    .select {
      &__field {
        background-color: $c-gray-1 !important;
      }

      &__arrow {
        transform: rotate(0deg) translateY(-50%) !important;
      }

      &__dropdown {
        visibility: visible;
        opacity: 1;
      }
    }

    .select__label {
      color: $c-gray-dark;
    }
    //.select__label {
    //  top: 8px;
    //  left: 16px;
    //  font-size: 13px;
    //  transform: translateY(0);
    //}
  }

  &.no-label {
    .select__text {
      top: 0;
    }
  }

  .select__field-wrapper.selected {
    .select {
      &__field {
        //background-color: $c-gray-1 !important;
      }

      //&__arrow {
      //  transform: rotate(180deg) translateY(-50%);
      //}

      &__dropdown {
        visibility: visible;
        opacity: 1;
      }
    }

    .select__text {
      opacity: 1;
      visibility: visible;
    }

    .select__label {
      color: $c-gray-7 !important;
    }
  }
}
</style>
